import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUEST_WISHLIST_KEY = 'nepora_guest_wishlist';

export const WishlistProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load wishlist on mount or user change
  useEffect(() => {
    if (user && token) {
      fetchWishlist();
    } else {
      // Load from local storage for guests
      loadGuestWishlist();
    }
  }, [user, token]);

  const loadGuestWishlist = async () => {
    const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
    if (stored) {
      try {
        const productIds = JSON.parse(stored);
        setWishlistItems(productIds.map(id => ({ product_id: id })));
        // Fetch product details
        if (productIds.length > 0) {
          const products = await Promise.all(
            productIds.map(async (id) => {
              try {
                const res = await axios.get(`${API}/products/${id}`);
                return res.data;
              } catch {
                return null;
              }
            })
          );
          setWishlistProducts(products.filter(Boolean));
        }
      } catch (e) {
        console.error('Failed to load guest wishlist', e);
      }
    }
  };

  const fetchWishlist = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistItems(response.data.items || []);
      setWishlistProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch wishlist', error);
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = useCallback(async (productId) => {
    if (user && token) {
      // Logged in user - use API
      try {
        await axios.post(`${API}/wishlist/${productId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchWishlist();
        return true;
      } catch (error) {
        console.error('Failed to add to wishlist', error);
        return false;
      }
    } else {
      // Guest user - use local storage
      const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
      let productIds = stored ? JSON.parse(stored) : [];
      if (!productIds.includes(productId)) {
        productIds.push(productId);
        localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(productIds));
        await loadGuestWishlist();
      }
      return true;
    }
  }, [user, token]);

  const removeFromWishlist = useCallback(async (productId) => {
    if (user && token) {
      // Logged in user - use API
      try {
        await axios.delete(`${API}/wishlist/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchWishlist();
        return true;
      } catch (error) {
        console.error('Failed to remove from wishlist', error);
        return false;
      }
    } else {
      // Guest user - use local storage
      const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
      let productIds = stored ? JSON.parse(stored) : [];
      productIds = productIds.filter(id => id !== productId);
      localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(productIds));
      await loadGuestWishlist();
      return true;
    }
  }, [user, token]);

  const isInWishlist = useCallback((productId) => {
    return wishlistItems.some(item => item.product_id === productId);
  }, [wishlistItems]);

  const toggleWishlist = useCallback(async (productId) => {
    if (isInWishlist(productId)) {
      return await removeFromWishlist(productId);
    } else {
      return await addToWishlist(productId);
    }
  }, [isInWishlist, addToWishlist, removeFromWishlist]);

  // Sync guest wishlist to user account after login
  const syncGuestWishlistToUser = useCallback(async () => {
    if (!user || !token) return;
    
    const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
    if (stored) {
      const productIds = JSON.parse(stored);
      for (const productId of productIds) {
        try {
          await axios.post(`${API}/wishlist/${productId}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          // Ignore errors for individual items
        }
      }
      localStorage.removeItem(GUEST_WISHLIST_KEY);
      await fetchWishlist();
    }
  }, [user, token]);

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      wishlistProducts,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      syncGuestWishlistToUser,
      refreshWishlist: fetchWishlist,
      wishlistCount: wishlistItems.length
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
